import {ArrayUtils} from '@colonist/utils'
import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import {type UIGameMechanic} from '../UIGameEventController.ts'
import {type CardPopupInformation} from '@colonist/game-card-types'
import {type UIGameMapDiceProbabilityView} from './UIGameMapDiceProbabilityView.ts'
import {Sound} from '../../Sound.ts'
import {ActionState, CardEnum} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {CARD_POPUP_BODY} from '@colonist/ui-game'

export interface InventorActionBoxData {
    onCheckAction: () => void
    onCancelAction: () => void
    popupInformation: CardPopupInformation
}

export class UIGameInventorHighlightDiceController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    private diceProbabilityViewsBeingHighlighted: UIGameMapDiceProbabilityView[]
    private selectedDiceNumbers: UIGameMapDiceProbabilityView[]

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.diceProbabilityViewsBeingHighlighted = []
        this.selectedDiceNumbers = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.HighlightDiceNumbersForInventor: this.highlightDiceNumbers(data); break
            case UIGameEventEnums.GameStateUpdated: this.gameStateUpdated(); break
            default: break
        }
    }

    private highlightDiceNumbers(highlightedIndexes: number[]) {
        const isAlreadyHighlighted = this.diceProbabilityViewsBeingHighlighted.length != 0
        if(isAlreadyHighlighted) return

        for(const highlightIndex of highlightedIndexes) {
            const tileView = this.uiGameManager.mapController.tileViews[highlightIndex]
            if(tileView.probabilityView == undefined) continue

            tileView.probabilityView.highlight()
            this.diceProbabilityViewsBeingHighlighted.push(tileView.probabilityView)
        }
        this.startAnimation()
        this.showInventorConfirmationPopup()
    }

    private startAnimation() {
        this.animateDiceProbabilityViewsIfUnselected()
        this.setClickAction()
    }

    private animateDiceProbabilityViewsIfUnselected() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            const diceProbabilityViewIsSelected = ArrayUtils.elementInArray(this.selectedDiceNumbers, diceProbabilityView)
            if(diceProbabilityViewIsSelected) continue

            diceProbabilityView.resetAnimation()
            diceProbabilityView.animate()
        }
    }

    private setClickAction() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            diceProbabilityView.eventMode = 'static'
            diceProbabilityView.on('pointerdown', () => {
                this.clickedDiceNumber(diceProbabilityView)
            })
        }
    }

    private endAnimation() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) diceProbabilityView.resetAnimation()
        this.removeClickAction()
    }

    private removeClickAction() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            diceProbabilityView.removeAllListeners('pointerdown')
        }
    }

    private clickedDiceNumber(diceProbabilityView: UIGameMapDiceProbabilityView) {
        Sound.playSound(Sound.SoundType.Click)
        const alreadySelectedDiceView = ArrayUtils.elementInArray(this.selectedDiceNumbers, diceProbabilityView)
        alreadySelectedDiceView ? this.deselectDiceNumber(diceProbabilityView) : this.selectDiceNumber(diceProbabilityView)
    }

    private selectDiceNumber(diceProbabilityView: UIGameMapDiceProbabilityView) {
        const alreadySelectedTwoNumbers = this.selectedDiceNumbers.length == 2
        if(alreadySelectedTwoNumbers) return

        this.selectedDiceNumbers.push(diceProbabilityView)
        diceProbabilityView.removeHighlight()
        diceProbabilityView.resetAnimation()
        this.updateCheckButtonOpacity()
    }

    private deselectDiceNumber(diceProbabilityView: UIGameMapDiceProbabilityView) {
        ArrayUtils.removeElementFromArray(this.selectedDiceNumbers, diceProbabilityView)
        this.animateDiceProbabilityViewsIfUnselected()
        diceProbabilityView.highlight()
        this.updateCheckButtonOpacity()
    }

    private updateCheckButtonOpacity() {
        const hasActivePopup = this.uiGameManager.gameStore.getState().actionBox.actionBoxData != undefined
        if(!hasActivePopup) return

        const amountOfSelectDiceNumbers = this.selectedDiceNumbers.length
        const isConfirmDisabled = amountOfSelectDiceNumbers !== 2

        const currentActionBoxData = this.uiGameManager.gameStore.getState().actionBox.actionBoxData
        const isShowingBaseActionBox = currentActionBoxData && currentActionBoxData.type === 'base'
        if(isShowingBaseActionBox) {
            this.uiGameManager.gameStore.getState().actionBox.setActionBoxData({
                ...currentActionBoxData,
                props: {
                    ...currentActionBoxData.props,
                    isConfirmDisabled: isConfirmDisabled,
                },
            })
        }
    }

    private showInventorConfirmationPopup() {
        const hasActivePopup = this.uiGameManager.gameStore.getState().actionBox.actionBoxData != undefined
        if(hasActivePopup) return

        const cardConfirmationPopupFormat: CardPopupInformation = {
            cardEnum: CardEnum.ProgressCardScienceInventor,
            cardBody: CARD_POPUP_BODY[CardEnum.ProgressCardScienceInventor],
            allowableActionState: ActionState.SelectTileProductionNumbersToSwap,
        }

        const inventorPopupData: InventorActionBoxData = {
            onCheckAction: () => this.checkAction(),
            onCancelAction: () => this.cancelAction(),
            popupInformation: cardConfirmationPopupFormat,
        }

        this.uiGameManager.eventController.sendEvent(inventorPopupData, UIGameEventEnums.SelectDiceNumbersConfirmation)
        this.updateCheckButtonOpacity()
    }

    private checkAction() {
        const alreadySelectedTwoNumbers = this.selectedDiceNumbers.length == 2
        if(!alreadySelectedTwoNumbers) return

        const selectedTiles = this.getSelectedTileIndices()

        this.uiGameManager.socketGameSend.selectedTiles(selectedTiles)
        this.closeUI()
    }

    private getSelectedTileIndices(): number[] {
        const selectedTileIndexs: number[] = []
        for(const selectedDiceNumber of this.selectedDiceNumbers) {
            const tileIndex = this.uiGameManager.gameState.mapState.tileState.getTileHexIndex(selectedDiceNumber.hexFace)
            if(tileIndex == undefined) continue

            selectedTileIndexs.push(tileIndex)
        }
        return selectedTileIndexs
    }

    private cancelAction() {
        this.uiGameManager.socketGameSend.cancelAction()
        this.closeUI()
    }

    private closeUI() {
        const hasActivePopup = this.uiGameManager.gameStore.getState().actionBox.actionBoxData != undefined
        if(hasActivePopup) return
        this.removeHighlights()
        this.endAnimation()
        this.selectedDiceNumbers = []
        this.diceProbabilityViewsBeingHighlighted = []
        this.uiGameManager.gameStore.getState().actionBox.hideActionBox()
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.PopupActive)
    }

    private removeHighlights() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            diceProbabilityView.removeHighlight()
        }
    }

    private gameStateUpdated() {
        if(this.uiGameManager.gameController.currentState.actionState == ActionState.SelectTileProductionNumbersToSwap) return
        this.closeUI()
    }
}
